import { GeneratedPodcastStageStatus } from '@kimuno/api-sdk';
import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Alert, AlertTitle, Box, SvgIcon, Typography } from '@mui/material';
import type React from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { sharingLinks } from '../../../constants';
import { getCurrentConversation } from '../../../reducers/aiAssistant.selectors';
import services from '../../../services';
import { aiAssistantEditorContext } from '../../../state/AIAssistantEditorProvider';
import { escapeHtml } from '../../../utils/escapeHtml';
import getMuiIcon from '../../../utils/getMuiIcon';
import { convertMarkdownToHtml } from '../../utils/contentConverter';
import AIAssistantTextEditorCopyButton from '../shared/TextEditor/AIAssistantTextEditorCopyButton';
import AIAssistantTextEditorExportButton, {
	type TextEditorDownloadFormat,
} from '../shared/TextEditor/AIAssistantTextEditorExportButton';
import usePodcastAudioActionTracking from '../shared/usePodcastAudioActionTracking';
import PodcastAudioPlayer from './PodcastAudioPlayer';

const containerStyles = {
	display: 'flex',
	flexDirection: 'column',
	gap: spaces.lg,
	padding: spaces.lg,
	width: '100%',
	boxSizing: 'border-box',
};

const transcriptHeaderStyles = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: spaces.sm,
	flexWrap: 'wrap',
};

const transcriptTitleStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: spaces.xs,
};

const transcriptActionsStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: spaces.sm,
};

const transcriptCardStyles = {
	width: '100%',
	padding: spaces.md,
	border: `1px solid ${palette.divider}`,
	borderRadius: borderRadius.md,
	background: palette.background.paperElevation2,
	boxSizing: 'border-box',
};

// The questions deliberately bypass the markdown converter: Showdown turns
// `1. …` lines into an <ol> and drops the digits, so the numbers would survive
// only as CSS list markers — absent from the plain-text flavor entirely, and
// lost in any paste target that flattens lists. Keeping them as literal text
// makes the clipboard match what the section renders on screen.
const questionsToClipboardHtml = (questions: string): string =>
	questions
		.split('\n')
		.map((line) => `<p>${escapeHtml(line)}</p>`)
		.join('\n');

const PodcastDetails: React.FC = () => {
	const { t } = useTranslation();
	const {
		podcastDetails,
		autoPlayPodcastId,
		consumeAutoPlayPodcast,
		podcastPauseRequested,
		consumePodcastPauseRequest,
		reportPodcastPlaybackState,
	} = useContext(aiAssistantEditorContext);
	const currentConversation = useSelector(getCurrentConversation);
	const currentConversationId = currentConversation?.data?.id;
	const { handleAudioPlayTrack, handleAudioPauseTrack } = usePodcastAudioActionTracking(
		podcastDetails?.id
	);

	const transcriptHtml = useMemo(
		() => (podcastDetails ? convertMarkdownToHtml(podcastDetails.transcript) : ''),
		[podcastDetails]
	);

	const comprehensionQuestionsHtml = useMemo(
		() =>
			podcastDetails?.comprehensionQuestions
				? questionsToClipboardHtml(podcastDetails.comprehensionQuestions)
				: '',
		[podcastDetails]
	);

	const trackTranscriptExport = (
		exportType: 'download' | 'share' | 'print',
		exportFormat?: TextEditorDownloadFormat
	) => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType,
			exportFormat,
			feature: ['podcast generation'],
		});
	};

	const handleTranscriptDownload = (format: TextEditorDownloadFormat) => {
		trackTranscriptExport('download', format);
	};

	const handleTranscriptShare = () => {
		trackTranscriptExport('share');
	};

	const handleTranscriptPrint = () => {
		trackTranscriptExport('print', 'pdf');
	};

	if (!podcastDetails) {
		return null;
	}

	// Skip empty parts (pre-params legacy podcasts, or an API not yet returning
	// the metadata fields) so the row never renders as bare separators (WOR-977).
	const metadataLabel = [
		podcastDetails.subject,
		podcastDetails.grade,
		podcastDetails.language,
		podcastDetails.format,
	]
		.filter(Boolean)
		.join(' • ');

	const showAudioError = Boolean(podcastDetails.audioGenerationFailed) || !podcastDetails.audioUrl;
	const shouldAutoPlay =
		// The id (not a boolean) guarantees a switch intent can never fire against the
		// previous podcast's details, which linger until the fetch resolves.
		autoPlayPodcastId === podcastDetails.id &&
		podcastDetails.audioStatus === GeneratedPodcastStageStatus.READY &&
		// defensive: the player isn't rendered on error, but keep the gate self-consistent.
		!showAudioError;

	return (
		<Box sx={containerStyles} data-testid="podcast-details">
			{metadataLabel && (
				<Typography
					variant="body2"
					sx={{ color: palette.text.secondary }}
					data-testid="podcast-details-metadata"
				>
					{metadataLabel}
				</Typography>
			)}

			{showAudioError ? (
				<Alert severity="warning" data-testid="podcast-audio-error-alert">
					<AlertTitle sx={{ color: palette.warning.main }}>{t('Audio could not be loaded')}</AlertTitle>
					<Typography variant="body2" sx={{ color: palette.warning.main }}>
						{t('The audio file could not be loaded. However, the transcript is still available to you.')}
					</Typography>
				</Alert>
			) : (
				<PodcastAudioPlayer
					src={podcastDetails.audioUrl}
					durationLabelFallback={podcastDetails.durationLabel}
					onPlay={handleAudioPlayTrack}
					onPause={handleAudioPauseTrack}
					autoPlay={shouldAutoPlay}
					onAutoPlayConsumed={consumeAutoPlayPodcast}
					pauseRequested={podcastPauseRequested}
					onPauseRequestConsumed={consumePodcastPauseRequest}
					onPlaybackStateChange={reportPodcastPlaybackState}
				/>
			)}

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.sm }}>
				<Box sx={transcriptHeaderStyles}>
					<Box sx={transcriptTitleStyles}>
						<SvgIcon
							component={getMuiIcon('transcript')}
							aria-hidden="true"
							sx={{ color: palette.text.secondary }}
						/>
						<Typography variant="subtitle1" sx={{ color: palette.text.primary }}>
							{t('Transcript')}
						</Typography>
					</Box>
					<Box sx={transcriptActionsStyles}>
						<AIAssistantTextEditorCopyButton
							content={transcriptHtml}
							dataTestId="podcast-details-copy-transcript"
							sx={{ color: palette.primary.main }}
						/>
						<AIAssistantTextEditorExportButton
							variant="outlined"
							label={t('Export transcript')}
							id="podcast-details-export-transcript-button"
							dataTestId="podcast-details-export-transcript"
							showNotification={false}
							menuAnchorOrigin="right"
							content={transcriptHtml}
							shareConfig={{
								createLink: async () => {
									const { accessToken } = await services.ai.createPublicPodcastLink(podcastDetails.id);
									return `${sharingLinks.podcastTranscript}/${accessToken}`;
								},
								dialogTitle: t('Share this transcript with your students'),
								qrImageName: podcastDetails.title,
							}}
							onDownload={handleTranscriptDownload}
							onShare={handleTranscriptShare}
							onPrint={handleTranscriptPrint}
						/>
					</Box>
				</Box>

				<Box sx={transcriptCardStyles}>
					<Typography
						variant="body2"
						component="div"
						sx={{ color: palette.text.primary, whiteSpace: 'pre-wrap' }}
					>
						{podcastDetails.transcript}
					</Typography>
				</Box>
			</Box>

			{/* One condition covers all three absent cases: the teacher opted out,
			    the non-fatal questions stage failed, or the podcast predates WF-75. */}
			{podcastDetails.comprehensionQuestions && (
				<Box
					sx={{ display: 'flex', flexDirection: 'column', gap: spaces.sm }}
					data-testid="podcast-details-comprehension-questions"
				>
					<Box sx={transcriptHeaderStyles}>
						<Box sx={transcriptTitleStyles}>
							<SvgIcon
								component={getMuiIcon('messageQuestion')}
								aria-hidden="true"
								sx={{ color: palette.text.secondary }}
							/>
							<Typography variant="subtitle1" sx={{ color: palette.text.primary }}>
								{t('Comprehension questions')}
							</Typography>
						</Box>
						<Box sx={transcriptActionsStyles}>
							<AIAssistantTextEditorCopyButton
								content={comprehensionQuestionsHtml}
								plainText={podcastDetails.comprehensionQuestions}
								dataTestId="podcast-details-copy-comprehension-questions"
								sx={{ color: palette.primary.main }}
							/>
						</Box>
					</Box>

					<Box sx={transcriptCardStyles}>
						<Typography
							variant="body2"
							component="div"
							sx={{ color: palette.text.primary, whiteSpace: 'pre-wrap' }}
						>
							{podcastDetails.comprehensionQuestions}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default PodcastDetails;
